import validateCpfOrCnpj from '@/utils/validateCpfOrCnpj';
import { isValidInternationalPhoneNumber } from '@/utils/phone';
import { POSTAL_CODE_RULES } from '@/constants/customer-international-bank-constants';

const mixinValidationRules = {
  data() {
    return {};
  },
  methods: {
    validationIsRequiredField(value) {
      return !!value || `Este campo é obrigatório`;
    },
    validationIsRequiredAutocompleteId(value) {
      return !!value?.id || `Este campo é obrigatório`;
    },
    validationIsRequiredAutocompleteText(value) {
      return !!value || `Este campo é obrigatório`;
    },
    validationIsRequiredAutocompleteMultiple(value) {
      return !!value?.length || `É obrigatório selecionar pelo menos uma das opções`;
    },
    validationIsRequiredRadioBtn(value) {
      return !!value || value == false || `É obrigatório selecionar uma das opções`;
    },
    /**
     * Uma regex mais segura e suficiente para validação de e-mail comum, sem tentar cobrir todos os casos complexos do RFC 5322.
     * documentação https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email#validation
     * @param {String} value
     * @returns {Boolean|String} Retorna true se o e-mail for válido ou uma mensagem de erro se não for.
     */
    validationIsEmailValid(value) {
      return !value || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) || 'Informe um e-mail válido';
    },
    validationIsCpfCnpjValid(value) {
      if (!value || value.length == 0) {
        return true;
      }

      let valid = false;

      if (value.length == 14) {
        valid = validateCpfOrCnpj.validateCPF(value);
      } else if (value.length == 18) {
        valid = validateCpfOrCnpj.validateCNPJ(value);
      }

      return valid || 'Informe um cpf/cnpj válido';
    },
    validationIsRequiredFieldNumeric(value) {
      const number = parseFloat(
        value
          .toString()
          // removes letters
          .replace(/^\D+/g, '')
          // convert the string to a valid float format
          .replace(/\./g, '')
          .replace(',', '.')
      );

      return !!number || `Este campo é obrigatório`;
    },
    validationIsRequiredCheckBox(value) {
      return !!value || `É obrigatório concordar para prosseguir`;
    },
    validationIsRequiredFile(value) {
      return !!value?.name || `Este campo é obrigatório`;
    },
    validationPasswordField(value) {
      return (value && value.trim().length >= 6) || 'Senha deve ter mínimo de 6 caracteres';
    },
    validationIsRequiredFieldNumericAllowZero(value) {
      const number = parseFloat(
        value
          .toString()
          // removes letters
          .replace(/^\D+/g, '')
          // convert the string to a valid float format
          .replace(/\./g, '')
          .replace(',', '.')
      );

      return !!number || number == 0 || `Este campo é obrigatório`;
    },
    validationIsPhoneValid(value, countryCode) {
      if (!value) {
        return true;
      }

      if (!countryCode) {
        return 'DDI é obrigatório';
      }

      return (
        isValidInternationalPhoneNumber(countryCode, value) ||
        'Celular inválido para o DDI informado'
      );
    },
    /**
     * Retorna uma função de validação de telefone com DDI pré-configurado.
     */
    validationPhoneWithDdi(countryCode) {
      return (value) => this.validationIsPhoneValid(value, countryCode);
    },
    /**
     * Valida código SWIFT/BIC (8 ou 11 caracteres alfanuméricos)
     * Formato: AAAABBCC ou AAAABBCCDDD
     */
    validationIsSwiftCodeValid(value) {
      if (!value) {
        return true;
      }

      const swiftRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/i;

      return swiftRegex.test(value) || 'Código SWIFT/BIC deve ter 8 ou 11 caracteres (ex: CHASUS33 ou CHASUS33XXX)';
    },
    /**
     * Valida CLABE mexicana (18 dígitos)
     */
    validationIsClabeValid(value) {
      if (!value) return true;

      const digitsOnly = value.toString().replace(/\D/g, '');

      return digitsOnly.length === 18 || 'CLABE deve ter exatamente 18 dígitos';
    },
    /**
     * Valida número de cartão de débito SPEI (16 dígitos)
     */
    validationIsDebitCardNumberValid(value) {
      if (!value) return true;

      const digitsOnly = value.toString().replace(/\D/g, '');

      return digitsOnly.length === 16 || 'Número do cartão deve ter exatamente 16 dígitos';
    },
    /**
     * Valida CVU argentino (22 dígitos)
     */
    validationIsCvuValid(value) {
      if (!value) {
        return true;
      }

      const digitsOnly = value.toString().replace(/\D/g, '');

      return digitsOnly.length === 22 || 'CVU deve ter exatamente 22 dígitos';
    },
    /**
     * Valida CBU argentino (22 dígitos)
     */
    validationIsCbuValid(value) {
      if (!value) {
        return true;
      }

      const digitsOnly = value.toString().replace(/\D/g, '');

      return digitsOnly.length === 22 || 'CBU deve ter exatamente 22 dígitos';
    },
    /**
     * Valida CVU ou CBU argentino (22 dígitos)
     */
    validationIsCvuCbuValid(value) {
      if (!value) {
        return true;
      }

      const digitsOnly = value.toString().replace(/\D/g, '');

      return digitsOnly.length === 22 || 'CVU/CBU deve ter exatamente 22 dígitos';
    },
    /**
     * Valida código postal de acordo com o país selecionado.
     * Se o país tem regra específica, aplica regex do país.
     * Caso contrário, aplica validação genérica (3-10 chars alfanuméricos com pelo menos 1 dígito).
     */
    validationIsPostalCodeValid(value, countryCode) {
      if (!value) return true;

      const cleaned = value.toString().trim();

      if (countryCode) {
        const rule = POSTAL_CODE_RULES[countryCode.toUpperCase()];

        if (rule) return rule.regex.test(cleaned) || `Código postal inválido para o país selecionado (ex: ${rule.example})`;
      }

      const genericRegex = /^[A-Za-z0-9][A-Za-z0-9\s-]{1,8}[A-Za-z0-9]$/;

      if (!genericRegex.test(cleaned)) return 'Código postal inválido (mínimo 3 caracteres alfanuméricos)';

      if (!/\d/.test(cleaned)) return 'Código postal deve conter pelo menos um número';

      return true;
    },
    /**
     * Retorna uma função de validação de código postal com país pré-configurado.
     */
    validationPostalCodeWithCountry(countryCode) {
      return (value) => this.validationIsPostalCodeValid(value, countryCode);
    }
  }
};

export default mixinValidationRules;
